import classNames from "classnames";
import { isFunction } from "lodash-es";
import React, { FC, KeyboardEvent, MouseEvent } from "react";
import Modal, { Props as ModalProps } from "react-modal";

import { Animation, AnimationTarget } from "../Animations";
import { IStyleSize, overflowClass, TStyleOverflowValue } from "../Style";
import { IModalContainer } from "./types";

/**
 * react-modal docs: http://reactcommunity.org/react-modal/
 */

export const setAppElement = Modal.setAppElement;
export interface IDialogModal
  extends IModalContainer,
    Partial<IStyleSize>,
    Partial<
      Pick<
        ModalProps,
        /**
         * Function that will be run after the modal has opened.
         */
        | "onAfterOpen"
        /**
         * Boolean indicating if the modal should be focused after render
         * @default true
         */
        | "shouldFocusAfterRender"
        /**
         * Boolean indicating if the overlay should close the modal
         * @default true
         */
        | "shouldCloseOnOverlayClick"
        /**
         * Boolean indicating if pressing the esc key should close the modal
         * Note: By disabling the esc key from closing the modal you may introduce an accessibility issue.
         */
        | "shouldCloseOnEsc"
      >
    > {
  /**
   * Function that will be run when before close a modal.
   * If the function returns false the modal is not be closed.
   */
  onRequestClose?: (e?: MouseEvent | KeyboardEvent) => void | boolean;
  overflowX?: TStyleOverflowValue;
  overflowY?: TStyleOverflowValue;
  fixedHeight?: boolean;
}

export function inDialogModal<P extends IModalContainer>(Component: React.ComponentType<P>) {
  const DialogModal: FC<IDialogModal & P> = props => {
    const {
      animated = true,
      animation = "fade-grow",
      fixedHeight,
      onAfterOpen,
      onClose,
      onRequestClose,
      open,
      overflowX,
      overflowY,
      shouldCloseOnEsc,
      shouldCloseOnOverlayClick,
      shouldFocusAfterRender,
      size,
      ...proxy
    } = props;

    const backdropBaseClass = "modal-dialog__backdrop";
    const containerBaseClass = "modal-dialog__container";

    const containerClasses = classNames(
      containerBaseClass,
      overflowClass(overflowX || "hidden", "x"),
      overflowClass(overflowY || "auto", "y"),
      {
        [`${containerBaseClass}--size-${size}`]: !!size,
        [`${containerBaseClass}--fixed-height`]: !!fixedHeight,
      },
    );

    const closeModal = (close: () => void) => (e: MouseEvent | KeyboardEvent) => {
      if (isFunction(onRequestClose) && onRequestClose(e) === false) {
        return;
      }

      close();
    };

    return (
      <Animation animate={open} onFinish={onClose}>
        {(visible, finish) => (
          <Modal
            ariaHideApp={false}
            className={containerClasses}
            closeTimeoutMS={300}
            isOpen={open}
            onRequestClose={closeModal(finish)}
            onAfterOpen={onAfterOpen}
            overlayClassName={backdropBaseClass}
            shouldCloseOnEsc={shouldCloseOnEsc}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            shouldFocusAfterRender={shouldFocusAfterRender}>
            <AnimationTarget name={animation} disabled={!animated}>
              <Component {...(proxy as P)} open={visible} onClose={closeModal(finish)} />
            </AnimationTarget>
          </Modal>
        )}
      </Animation>
    );
  };

  return DialogModal;
}
